/**
 * Short backoff retries for localhost preview fetches (preview environments restart, HMR, brief overload).
 * Separate from WebSocket reconnect delays — these are sub-second for snappy UX.
 */

export const LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS = [30, 100, 200, 400] as const;

export function sleepMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Flatten `Error.cause` chains into one lowercase string for matching. */
function collectErrorText(err: unknown): string {
  const parts: string[] = [];
  let e: unknown = err;
  for (let depth = 0; depth < 6 && e != null; depth += 1) {
    if (e instanceof Error) {
      parts.push(e.message);
      e = e.cause;
    } else {
      parts.push(String(e));
      break;
    }
  }
  return parts.join(' ').toLowerCase();
}

/**
 * True for errors that often clear if we wait a few ms and retry (localhost only).
 */
export function isTransientLocalServiceError(err: unknown): boolean {
  const text = collectErrorText(err);
  if (!text) return false;
  return (
    text.includes('fetch failed') ||
    text.includes('econnrefused') ||
    text.includes('econnreset') ||
    text.includes('epipe') ||
    text.includes('etimedout') ||
    text.includes('socket hang up') ||
    text.includes('network connection lost') ||
    text.includes('ecanceled') ||
    text.includes('aborted') ||
    text.includes('und_err_socket') ||
    text.includes('other side closed') ||
    text.includes('eai_again')
  );
}

function isIdempotentHttpMethod(method: string): boolean {
  const m = method.toUpperCase();
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
}

export interface FetchWithLocalRetryOptions {
  /** Defaults to 5 for idempotent methods, 1 otherwise. */
  maxAttempts?: number;
  delaysMs?: readonly number[];
}

/**
 * `fetch` to localhost with a few quick retries on transient failures (GET/HEAD/OPTIONS only).
 */
export async function fetchWithLocalTransientRetries(
  url: string,
  init: RequestInit | undefined,
  options?: FetchWithLocalRetryOptions,
): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const allowRetry = isIdempotentHttpMethod(method);
  const delays = options?.delaysMs ?? LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS;
  const maxAttempts =
    options?.maxAttempts ?? (allowRetry ? Math.max(1, delays.length + 1) : 1);

  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await fetch(url, init);
    } catch (e) {
      lastErr = e;
      const canRetry = allowRetry && attempt < maxAttempts - 1 && isTransientLocalServiceError(e);
      if (!canRetry) throw e;
      const waitMs = delays[Math.min(attempt, delays.length - 1)] ?? 100;
      await sleepMs(waitMs);
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}
